import React from "react";
import axios from "axios";
import moment from "moment";
import { Modal, Popconfirm, Spin, message } from "antd";
import { MdContentCopy, MdOutlineRemoveRedEye } from "react-icons/md";
import { useMutation, useQuery, useQueryClient } from "react-query";

import ContentBlock from "../../../../components/Layout/ContentBlock";
import FilterMenu from "../../../../components/Layout/FilterMenu";
import {
  ButtonSecondary,
  InputSwitchComponent,
  Pagination,
  TextInput,
} from "../../../../components/globals";
import TableFilterBox from "../../../../components/common/TableFilterBox";
import { ButtonEditRow, SelectForm } from "../../../../components/common/Buttons";
import RangePickerComp from "../../../../components/common/RangePickerComp";
import { DEFAULT_PER_PAGE } from "../../../../helpers/default";
import { prettyDateFormat } from "../../../../helpers/Utils";
import { useBulkSelection, useModal } from "../../../../hooks";
import {
  BulkActionBar,
  BulkHeaderCheckbox,
  BulkRowCheckbox,
} from "../../../../components/common/BulkSelection";
import ModalNewInfoZone from "../modals/ModalNewInfoZone";
import InfoZoneIcon from "./InfoZoneIcon";
import InfoZoneManagerTabUi from "./InfoZoneManagerTabUi";
import { showApiError } from "./apiErrors";
import {
  PREVIEW_SANDBOX,
  previewSrc,
  renderInfoZonePreview,
} from "./blockPreview";

const ZONE_TYPE_LABELS = {
  category: "Category",
  product: "Product",
  page: "Page",
  brand: "Brand",
  home: "Titulinis",
};

const ZONE_POSITION_LABELS = {
  header: "Header",
  top: "Top",
  bottom: "Bottom",
  footer: "Footer",
  inline: "Inline",
  description: "Description",
};

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Enabled", value: "true" },
  { label: "Disabled", value: "false" },
];

const buildInfoZoneQuery = ({ page, perPage, filters }) => {
  const params = new URLSearchParams();
  params.append("page", page);
  params.append("per_page", perPage);

  if (filters.title) {
    params.append("q[title_i_cont]", filters.title);
  }
  if (filters.shop) {
    params.append("q[banner_shops_order_shop_id_eq]", filters.shop);
  }
  if (Array.isArray(filters.categories) && filters.categories.length) {
    filters.categories.forEach((categoryId) => {
      params.append("q[categories_raw_cont_any][]", `- ${categoryId}\n`);
    });
  }
  if (Array.isArray(filters.datePeriod) && filters.datePeriod.length === 2) {
    params.append("q[valid_to_gteq]", filters.datePeriod[0]);
    params.append("q[valid_from_lteq]", filters.datePeriod[1]);
  }
  if (filters.status) {
    params.append("q[info_zone_status_eq]", filters.status);
  }

  return params.toString();
};

const formatPeriod = (validFrom, validTo) => {
  const fromLabel = validFrom ? prettyDateFormat(validFrom) : "No start date";
  const toLabel = validTo ? prettyDateFormat(validTo) : "No end date";
  return `${fromLabel} - ${toLabel}`;
};

const getDateFormat = (date) => {
  const normalizedDate = moment(date);
  return date && normalizedDate.isValid() ? normalizedDate : "";
};

const INITIAL_PREVIEW_HEIGHT = 320;
const FALLBACK_PREVIEW_HEIGHT = 1600;
const PREVIEW_HEIGHT_TIMEOUT = 1500;

function InfoZonesTab({ tabId, setTabId }) {
  const queryClient = useQueryClient();
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(DEFAULT_PER_PAGE);
  const [filterTitle, setFilterTitle] = React.useState("");
  const [filterCategories, setFilterCategories] = React.useState([]);
  const [filterShop, setFilterShop] = React.useState("");
  const [filterDatePeriod, setFilterDatePeriod] = React.useState([]);
  const [filterStatus, setFilterStatus] = React.useState("");
  const [appliedFilters, setAppliedFilters] = React.useState({
    title: "",
    categories: [],
    shop: "",
    datePeriod: [],
    status: "",
  });
  const { modalOpt, setModalOpt, closeModal } = useModal();
  const [previewId, setPreviewId] = React.useState(null);
  const [previewHeight, setPreviewHeight] = React.useState(
    INITIAL_PREVIEW_HEIGHT
  );
  const previewFrame = React.useRef(null);
  const previewReported = React.useRef(false);

  React.useEffect(() => {
    const handleMessage = (event) => {
      if (
        !previewFrame.current ||
        event.source !== previewFrame.current.contentWindow ||
        event.data?.type !== "block-preview-height"
      ) {
        return;
      }

      const height = Number(event.data.height);
      if (!height) return;

      previewReported.current = true;
      setPreviewHeight(Math.min(Math.max(height, 120), 4000));
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const openPreview = (id) => {
    previewReported.current = false;
    setPreviewHeight(INITIAL_PREVIEW_HEIGHT);
    setPreviewId(id);
  };

  React.useEffect(() => {
    if (!previewId) return undefined;

    const timer = setTimeout(() => {
      if (!previewReported.current) setPreviewHeight(FALLBACK_PREVIEW_HEIGHT);
    }, PREVIEW_HEIGHT_TIMEOUT);

    return () => clearTimeout(timer);
  }, [previewId]);

  const { data: previewZone, isFetching: isLoadingPreview } = useQuery(
    ["info-zone-preview", previewId],
    async () => {
      const response = await axios.get(`/api/v1/banners/${previewId}`);
      return response.data;
    },
    {
      enabled: !!previewId,
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  );

  const { data: orderShops } = useQuery("info-zone-order-shops", async () => {
    const response = await axios.get("/api/v1/order_shops");
    return response.data;
  });

  const { data: categories } = useQuery("info-zone-categories", async () => {
    const response = await axios.get(
      "/api/v1/categories/simple_list?page=1&per_page=999999"
    );
    return response.data;
  });

  const duplicateMutation = useMutation({
    mutationFn: (id) => axios.post(`/api/v1/banners/${id}/duplicate`),
    onSuccess: () => {
      queryClient.invalidateQueries("info-zone-banners");
      queryClient.invalidateQueries("info-zone-blocks");
      message.success("Info zone duplicated");
    },
    onError: (error) => {
      showApiError(error, "Failed to duplicate info zone");
    },
  });

  const { data: banners, isLoading } = useQuery(
    ["info-zone-banners", page, perPage, appliedFilters],
    async () => {
      const query = buildInfoZoneQuery({
        page,
        perPage,
        filters: appliedFilters,
      });
      const response = await axios.get(`/api/v1/banners/info_zones?${query}`);
      return response.data;
    },
    {
      keepPreviousData: true,
    }
  );

  const categoryOptions = React.useMemo(
    () =>
      Array.isArray(categories)
        ? categories.map((category) => ({
            value: category.id,
            label: category.name,
          }))
        : [],
    [categories]
  );

  const shopOptions = React.useMemo(
    () => orderShops?.shops || [],
    [orderShops]
  );

  const rows = banners?.data || [];
  const totals = banners?.totals || 0;
  const selection = useBulkSelection(totals);

  const bulkDeleteMutation = useMutation({
    mutationFn: (payload) => {
      const query = buildInfoZoneQuery({ page, perPage, filters: appliedFilters });
      return axios.post(
        `/api/v1/banners/bulk_destroy?${query}&q[banner_place_eq]=info_zone`,
        payload
      );
    },
    onSuccess: () => {
      selection.clear();
      queryClient.invalidateQueries("info-zone-banners");
      queryClient.invalidateQueries("info-zone-blocks");
      queryClient.invalidateQueries("info-zone-block-zone-options");
      message.success("Info zones deleted");
    },
    onError: (error) => {
      showApiError(error, "Failed to delete info zones");
    },
  });

  const applyFilters = () => {
    selection.clear();
    setAppliedFilters({
      title: filterTitle,
      categories: filterCategories,
      shop: filterShop,
      datePeriod: filterDatePeriod,
      status: filterStatus,
    });
    setPage(1);
  };

  const handleFilterClear = () => {
    selection.clear();
    setFilterTitle("");
    setFilterCategories([]);
    setFilterShop("");
    setFilterDatePeriod([]);
    setFilterStatus("");
    setAppliedFilters({
      title: "",
      categories: [],
      shop: "",
      datePeriod: [],
      status: "",
    });
    setPage(1);
  };

  return (
    <>
      <ContentBlock>
        <FilterMenu
          text2="Banners"
          iconComponent={<InfoZoneIcon />}
          onChangeCountPage={setPerPage}
          countPage={perPage}
          page={page}
          totals={totals}
        />
      </ContentBlock>
      <div className="col-12">
        <InfoZoneManagerTabUi tabId={tabId} setTabId={setTabId} />
      </div>
      <ContentBlock containerClass="py-1">
        <TableFilterBox
          filter={true}
          onFilter={applyFilters}
          clearFilter={handleFilterClear}
          button1={
            <div className="float-left ml-2" style={{ width: 130 }}>
              <ButtonSecondary onClick={() => setModalOpt("new_info_zone", 0)}>
                New info zone
              </ButtonSecondary>
            </div>
          }
        >
          <div className="d-flex justify-content-between flex-1 flex-wrap">
            <div className="d-flex flex-wrap">
              <div className="px-1">
                <TextInput
                  text="Title"
                  name="Title"
                  style={{ width: 165 }}
                  value={filterTitle}
                  onChange={(e) => setFilterTitle(e.target.value)}
                />
              </div>
              <div className="px-1" style={{ width: 200 }}>
                <SelectForm
                  placeholder="Category"
                  mode="multiple"
                  options={categoryOptions}
                  selector="info_zone_category"
                  onlyPlaceholder="Select categories"
                  value={filterCategories}
                  onChange={(val) => setFilterCategories(val || [])}
                />
              </div>
              <div className="px-1" style={{ width: 165 }}>
                <SelectForm
                  placeholder="Shop"
                  options={shopOptions}
                  selector="info_zone_shop"
                  onlyPlaceholder="Shop"
                  value={filterShop}
                  onChange={(val) => setFilterShop(val)}
                />
              </div>
              <div className="px-1">
                <RangePickerComp
                  style={{ width: 250 }}
                  label="Date period"
                  placeholder={["Start date", "End date"]}
                  value={
                    filterDatePeriod?.length
                      ? [
                          getDateFormat(filterDatePeriod[0]),
                          getDateFormat(filterDatePeriod[1]),
                        ]
                      : ["", ""]
                  }
                  onChange={(_, value) => setFilterDatePeriod(value)}
                  disabledDate={() => false}
                />
              </div>
              <div className="px-1">
                <InputSwitchComponent
                  label="Status"
                  name="info_zone_status"
                  options={STATUS_OPTIONS}
                  value={filterStatus}
                  onChange={(val) => setFilterStatus(val.target.value)}
                />
              </div>
            </div>
          </div>
        </TableFilterBox>
        <BulkActionBar
          selection={selection}
          noun="info zones"
          loading={bulkDeleteMutation.isLoading}
          onConfirm={(payload) => bulkDeleteMutation.mutate(payload)}
        />

        <div className="table-responsive" style={{ overflow: "hidden" }}>
          <table className="table mt-3">
            <thead>
              <tr className="text-muted">
                <th style={{ width: 36 }}>
                  <BulkHeaderCheckbox selection={selection} disabled={!totals} />
                </th>
                <th className="text-black">Title</th>
                <th className="text-black">Shop</th>
                <th className="text-black">Valide period</th>
                <th className="text-black">Type</th>
                <th className="text-black">Position</th>
                <th className="text-black">Block</th>
                <th className="text-black"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => (
                <tr className="align-middle tableColumn" key={item.id}>
                  <td className="align-middle">
                    <BulkRowCheckbox selection={selection} id={item.id} />
                  </td>
                  <td className="align-middle">{item.title}</td>
                  <td className="align-middle">
                    <div className="d-flex flex-column" style={{ gap: 4 }}>
                      {(item.banner_shops || []).map((bannerShop) => (
                        <div key={`${item.id}-shop-${bannerShop.id}`}>
                          {bannerShop.order_shop?.title ||
                            `Shop #${bannerShop.order_shop_id}`}
                        </div>
                      ))}
                      {!item.banner_shops?.length && (
                        <div className="text-muted" style={{ fontSize: 13 }}>
                          All shops
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="align-middle">
                    <div className="d-flex flex-row align-items-center">
                      <div
                        className="dot"
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          marginRight: 8,
                          backgroundColor: item.active_now ? "#28A745" : "#C94747",
                        }}
                      />
                      {formatPeriod(item.valid_from, item.valid_to)}
                    </div>
                  </td>
                  <td className="align-middle">
                    {ZONE_TYPE_LABELS[item.popup_in] || "-"}
                  </td>
                  <td className="align-middle">
                    {ZONE_POSITION_LABELS[item.position] || "-"}
                  </td>
                  <td className="align-middle">
                    <div className="d-flex flex-column" style={{ gap: 4 }}>
                      {(item.block_labels || []).map((block, index) => (
                        <div
                          key={`${item.id}-block-${index}`}
                          style={{
                            background: "#F2F2F2",
                            color: "#454545",
                            borderRadius: 4,
                            padding: "4px 10px",
                            fontSize: 13,
                            display: "inline-block",
                            width: "fit-content",
                          }}
                        >
                          {block}
                        </div>
                      ))}
                      {!item.block_labels?.length && (
                        <div className="text-muted" style={{ fontSize: 13 }}>
                          No blocks
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="align-middle">
                    <div
                      className="d-flex justify-content-end align-items-center"
                      style={{ gap: 8 }}
                    >
                      <ButtonEditRow
                        className="d-flex justify-content-center align-items-center"
                        onClick={() => setModalOpt("edit_info_zone", item.id)}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-primary d-flex justify-content-center align-items-center"
                        title="Preview info zone"
                        onClick={() => openPreview(item.id)}
                        style={{ width: 28, height: 28, padding: 0 }}
                      >
                        <MdOutlineRemoveRedEye size={16} color="#fff" />
                      </button>
                      <Popconfirm
                        title="Duplicate this info zone?"
                        description="The copy uses the same blocks. It stays hidden on the eshop until you open and save it."
                        okText="Duplicate"
                        cancelText="Cancel"
                        onConfirm={() => duplicateMutation.mutate(item.id)}
                      >
                        <button
                          type="button"
                          className="btn btn-sm btn-primary d-flex justify-content-center align-items-center"
                          title="Duplicate info zone"
                          style={{ width: 28, height: 28, padding: 0 }}
                        >
                          <MdContentCopy size={14} color="#fff" />
                        </button>
                      </Popconfirm>
                    </div>
                  </td>
                </tr>
              ))}
              {!rows.length && !isLoading && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    No info zones found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <Pagination
            setPagination={setPage}
            page={page}
            totals={totals}
            pageCount={perPage}
          />
        </div>
      </ContentBlock>

      <Modal
        title={
          previewZone
            ? `${previewZone.title || "Info zone"} - ${
                (previewZone.banner_block_links || []).length
              } block(s)`
            : "Info zone preview"
        }
        visible={!!previewId}
        onCancel={() => setPreviewId(null)}
        footer={null}
        width={1100}
        bodyStyle={{ padding: 12, maxHeight: "75vh", overflowY: "auto" }}
      >
        {isLoadingPreview || !previewZone ? (
          <div className="text-center py-4">
            <Spin />
          </div>
        ) : (
          <iframe
            title="Info zone preview"
            src={previewSrc(renderInfoZonePreview(previewZone))}
            ref={previewFrame}
            sandbox={PREVIEW_SANDBOX}
            scrolling="auto"
            style={{
              width: "100%",
              height: previewHeight,
              border: "1px solid #EFEFEF",
              borderRadius: 4,
            }}
          />
        )}
      </Modal>

      {(modalOpt.type === "new_info_zone" ||
        modalOpt.type === "edit_info_zone") && (
        <ModalNewInfoZone onClose={closeModal} modalOpt={modalOpt} />
      )}
    </>
  );
}

export default InfoZonesTab;
